import swal from "sweetalert2";

import { GOCES, GocePermiso } from "../shared/model/permisos/permiso.model";
import { escapaHtml } from "./frc-firmado/historial-firmados";

/**
 * Lo que se le pregunta a quien autoriza antes de responder una solicitud,
 * igual para vacaciones y para permisos (29/10/2026, Norman: «al autorizar
 * un permiso salga bien ... que salga como el de vacaciones»). Hasta hoy el
 * permiso preguntaba «¿Cómo se autoriza?» con un botón por goce y su motivo
 * de rechazo iba sin decir de quién era; ahora las dos pantallas usan estas
 * dos ventanas. Quién firma y qué se resuelve lo sigue decidiendo cada una.
 */

/** Quien se queda sin nombre en la ventana (no debería pasar). */
const SIN_NOMBRE = "El colaborador";

/** Lo que se pregunta antes de autorizar. */
export interface Autorizacion {
  /** «¿Autorizar estas vacaciones?», «¿Autorizar este permiso?». */
  titulo: string;
  /** Quien lo pide: va en negritas. */
  nombre?: string;
  /** Lo pedido en una línea: el rango de días o el permiso en una frase. */
  detalle: string;
  /** Lo que va antes de la firma, armado en código y no por quien pide
   *  (puede llevar <b>): «Son <b>3</b> días hábiles.» */
  nota?: string;
  /** El formato que sella la firma. */
  documento: "FRC-019" | "FRC-017";
  /** El permiso, además, va con o sin goce de sueldo: lo decide quien
   *  autoriza, no quien pide, así que se escoge aquí. */
  conGoce?: boolean;
}

/** Las dos opciones del goce, sin ninguna marcada: es una decisión. */
const OPCIONES_GOCE =
  `<div style="display:flex;flex-wrap:wrap;gap:6px 22px;margin-top:12px" role="radiogroup" aria-label="Goce de sueldo">` +
  GOCES.map(
    (g) =>
      `<label style="display:inline-flex;align-items:center;gap:7px;cursor:pointer;font-size:14.5px;font-weight:500;color:#253042">` +
      `<input type="radio" name="goce" value="${g.valor}" style="width:17px;height:17px;margin:0;accent-color:#15803d">` +
      `${g.texto}</label>`
  ).join("") +
  `</div>`;

/** «Sí, autorizar» se enciende cuando ya se escogió el goce: así no hace falta
 *  el aviso de SweetAlert, que sale en gris y delgado. */
function enciendeAlEscoger(popup: HTMLElement): void {
  const boton = swal.getConfirmButton();
  if (!boton) {
    return;
  }
  boton.disabled = true;
  popup.querySelectorAll<HTMLInputElement>('input[name="goce"]').forEach((radio) =>
    radio.addEventListener("change", () => {
      boton.disabled = false;
    })
  );
}

/** El goce que quedó marcado; sin marcar (no debería pasar: el botón sigue
 *  apagado), la ventana no se cierra y lo pide. */
function goceMarcado(): GocePermiso | false {
  const marcado = swal.getHtmlContainer()?.querySelector<HTMLInputElement>('input[name="goce"]:checked');
  if (!marcado) {
    swal.showValidationMessage("Escoge si va con o sin goce de sueldo.");
    return false;
  }
  return marcado.value as GocePermiso;
}

/**
 * «¿Autorizar…?»: quién, qué, el goce si es permiso y el aviso de que sigue
 * la firma. Da null si se cancela; si se confirma, el goce escogido (sólo
 * con `conGoce`).
 */
export function confirmaAutorizar(a: Autorizacion): Promise<{ goce?: GocePermiso } | null> {
  return swal
    .fire({
      title: a.titulo,
      html:
        `<div style="text-align:left;line-height:1.7;font-size:15px">` +
        `<b>${escapaHtml(a.nombre || SIN_NOMBRE)}</b>` +
        `<div style="margin-top:6px">${escapaHtml(a.detalle)}</div>` +
        (a.conGoce ? OPCIONES_GOCE : "") +
        `<div style="margin-top:10px;padding-top:10px;border-top:1px solid #e6ebf2;` +
        `color:#475569;font-size:13.5px">${a.nota ? a.nota + " " : ""}` +
        `Al confirmar sigue tu firma, digital o en papel, que sella el ${a.documento}.</div></div>`,
      icon: "question",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Sí, autorizar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#15803d",
      didOpen: a.conGoce ? enciendeAlEscoger : undefined,
      preConfirm: a.conGoce ? goceMarcado : undefined,
    })
    .then((r) => {
      if (!r.isConfirmed) {
        return null;
      }
      return a.conGoce ? { goce: r.value as GocePermiso } : {};
    });
}

/**
 * «¿Por qué no se autoriza?»: el motivo es obligatorio, porque quien pidió lo
 * va a leer y un «no» a secas no le sirve para reacomodarse. Da el motivo, o
 * null si se cancela.
 */
export function pideMotivo(nombre: string | undefined, detalle: string, ejemplo: string): Promise<string | null> {
  return swal
    .fire({
      title: "¿Por qué no se autoriza?",
      html:
        `<div style="text-align:left;font-size:14px;color:#475569">` +
        `<b style="color:#253042">${escapaHtml(nombre || SIN_NOMBRE)}</b> · ${escapaHtml(detalle)}</div>`,
      input: "textarea",
      inputPlaceholder: ejemplo,
      inputAttributes: { maxlength: "300" },
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "No autorizar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#b71c1c",
      inputValidator: (valor) => (valor?.trim() ? null : "Hay que escribir el motivo."),
    })
    .then((r) => (r.isConfirmed ? String(r.value) : null));
}
